"use client"

import { ArrowRight02Icon } from "@hugeicons/core-free-icons"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  motion,
  useAnimation,
  useAnimationFrame,
  useMotionValue,
  useReducedMotion,
} from "motion/react"
import { useLayoutEffect, useRef, useState } from "react"
import { cn } from "@workspace/ui/lib/utils"
import { TextShimmer } from "@workspace/ui/components/text-shimmer"

const KNOB_SIZE = 48
const KNOB_INSET = 4
const KNOB_REST_OFFSET = KNOB_INSET - 1
const COMPLETION_RATIO = 0.92
const PERCENTAGE_MULTIPLIER = 100

export interface CallSwipeProps {
  className?: string
  disabled?: boolean
  duration?: number
  label?: string
  onCall?: () => void
}

export function CallSwipe({
  onCall,
  label = "Swipe to start a call",
  className,
  duration = 2000,
  disabled = false,
}: CallSwipeProps) {
  const x = useMotionValue(0)
  const controls = useAnimation()
  const trackRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLDivElement>(null)
  const shouldReduceMotion = useReducedMotion()
  const [maxDistance, setMaxDistance] = useState(0)

  useLayoutEffect(() => {
    const track = trackRef.current
    if (!track) return

    const update = () => {
      setMaxDistance(
        Math.max(0, track.offsetWidth - KNOB_SIZE - KNOB_INSET * 2)
      )
    }

    update()
    const observer = new ResizeObserver(update)
    observer.observe(track)
    return () => observer.disconnect()
  }, [])

  useAnimationFrame((t) => {
    if (shouldReduceMotion) return
    const progress = (t % duration) / duration
    textRef.current?.style.setProperty(
      "--x",
      `${(1 - progress) * PERCENTAGE_MULTIPLIER}%`
    )
  })

  const handleDragEnd = async () => {
    if (disabled || maxDistance === 0) {
      await controls.start({ x: 0 })
      return
    }

    const dragDistance = x.get()
    if (dragDistance >= maxDistance * COMPLETION_RATIO) {
      await controls.start({ x: maxDistance })
      onCall?.()
    } else {
      await controls.start({ x: 0 })
    }
  }

  const isInteractive = !disabled && !shouldReduceMotion && maxDistance > 0

  return (
    <div className={cn("flex w-full items-center justify-center", className)}>
      <div
        ref={trackRef}
        className="relative h-14 w-full rounded-full border-2 border-primary/80 ring-5 ring-primary/20 bg-secondary"
      >
        <div className="absolute inset-0 left-14 z-0 flex items-center justify-center overflow-hidden pr-4">
          <div
            ref={textRef}
            className="relative w-full select-none text-center font-medium text-lg"
          >
            <TextShimmer duration={2} repeatDelay={1}>
              {label}
            </TextShimmer>
          </div>
        </div>
        <motion.div
          animate={controls}
          aria-disabled={disabled}
          className={cn(
            "absolute z-10 flex h-12 w-12 items-center justify-center rounded-full bg-primary shadow-md",
            disabled
              ? "cursor-not-allowed opacity-50"
              : "cursor-grab active:cursor-grabbing"
          )}
          drag={isInteractive ? "x" : false}
          dragConstraints={{ left: 0, right: maxDistance }}
          dragElastic={0}
          dragMomentum={false}
          onDragEnd={handleDragEnd}
          style={{ left: KNOB_REST_OFFSET, x, top: "50%", y: "-50%" }}
          tabIndex={disabled ? -1 : 0}
          transition={
            shouldReduceMotion
              ? { duration: 0 }
              : { type: "spring", duration: 0.25 }
          }
        >
          <HugeiconsIcon
            icon={ArrowRight02Icon}
            size={24}
            className="text-primary-foreground"
          />
        </motion.div>
      </div>
    </div>
  )
}
